import { getUserSaveError } from "../utils/userManagementErrors";
import "./UserManagement.css";
import UserManagementIcon from "./UserManagementIcon";
import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Box,
  useTheme,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

const ActionFormDialog = ({
  open,
  onClose,
  onSave,
  mode = 'create',
  initialData = null,
}) => {
  const theme = useTheme();
  const [saveError, setSaveError] = useState("");
  const [saving, setSaving] = useState(false);
  const saveErrorRef = useRef(null);
  useEffect(() => { if (open) setSaveError(""); }, [open, mode, initialData]);
  useEffect(() => { if (saveError) saveErrorRef.current?.focus(); }, [saveError]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isSystemAction: false,
  });

  useEffect(() => {
    if (open && mode === 'edit' && initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        isSystemAction: initialData.isSystemAction || false,
      });
    } else if (open && mode === 'create') {
      setFormData({
        name: '',
        description: '',
        isSystemAction: false,
      });
    }
  }, [open, mode, initialData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (saving) return;
    setSaveError(""); setSaving(true);
    try { await onSave(formData); }
    catch (error) { setSaveError(getUserSaveError(error)); }
    finally { setSaving(false); }
  };

  const handleClose = () => {
    if (saving) return;
    setSaveError("");
    setFormData({
      name: '',
      description: '',
      isSystemAction: false,
    });
    onClose();
  };

  return (
    <Dialog
        className={`user-management-dialog ${theme.palette.mode === "dark" ? "user-management-dark" : ""}`}
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          pb: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <UserManagementIcon name="action" className="user-management-title-icon" />
        {mode === 'create' ? 'Create Action' : 'Edit Action'}
        <IconButton
          onClick={handleClose}
          aria-label="Close dialog"
          sx={{
            position: 'relative',
            right: -8,
            top: -8,
            color: 'white',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {saveError && <Alert severity="error" role="alert" tabIndex={-1} ref={saveErrorRef} sx={{mb:2}}>{saveError}</Alert>}
        <Box sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Action Name"
                value={formData.name}
                onChange={e => handleChange('name', e.target.value)}
                required
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={e => handleChange('description', e.target.value)}
                multiline
                rows={3}
                variant="outlined"
                helperText="Describe what this action does"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.isSystemAction}
                    onChange={e =>
                      handleChange('isSystemAction', e.target.checked)
                    }
                    disabled={mode === 'edit'}
                  />
                }
                label="System Action"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: '1px solid #eee' }}>
        <Button onClick={handleClose} color="inherit" disabled={saving}>
          <UserManagementIcon name="cancel" /> Cancel
        </Button>
        <Button
          disabled={saving}
          onClick={handleSave}
          variant="contained"
          sx={{
            bgcolor: theme.palette.primary.main,
            '&:hover': { bgcolor: theme.palette.primary.dark },
          }}
        >
          <UserManagementIcon name="tick" /> Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ActionFormDialog;
